import React, { useState } from 'react';
import { BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { getTooltipContent } from '../utils/tooltipContent.js';

/**
 * Universal tooltip component for displaying LaTeX formulas and explanations for dashboard metrics.
 * 
 * @param {Object} props
 * @param {string} props.metricKey - Required. Key identifying the metric (must match key in tooltipContent.js)
 * @param {'top'|'bottom'|'left'|'right'} [props.position='top'] - Optional. Position of tooltip popover relative to icon
 * @returns {JSX.Element | null} Tooltip component or null if metricKey not found
 */
function MetricTooltipComponent({ metricKey, position = 'top' }) {
  const [isVisible, setIsVisible] = useState(false);
  
  const content = getTooltipContent(metricKey);
  
  if (!content) {
    // Gracefully handle missing metricKey
    return null;
  }
  
  const handleMouseEnter = () => {
    setIsVisible(true);
  };
  
  const handleMouseLeave = () => {
    setIsVisible(false);
  };
  
  // Position styles for popover
  const positionStyles = {
    top: {
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: '8px',
    },
    bottom: {
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: '8px',
    },
    left: {
      right: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginRight: '8px',
    },
    right: {
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginLeft: '8px',
    },
  };
  
  const popoverStyle = {
    ...positionStyles[position],
    position: 'absolute',
    zIndex: 50,
    minWidth: '280px',
    maxWidth: '400px',
  };
  
  return (
    <div className="inline-flex items-center relative">
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="ml-1 cursor-pointer text-accent-orange hover:text-accent-orange-light transition-colors"
        aria-label={`Formula and explanation for ${metricKey}`}
        role="button"
        tabIndex={0}
      >
        ❓
      </span>
      {isVisible && (
        <div
          className="absolute bg-navy border border-dark p-4 rounded shadow-lg"
          style={popoverStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="mb-2">
            <BlockMath math={content.formula} />
          </div>
          <p className="text-primary text-sm leading-relaxed">
            {content.explanation}
          </p>
        </div>
      )}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export const MetricTooltip = React.memo(MetricTooltipComponent);

